import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Progress,
  Space,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  RocketOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import apiClient from "utils/apiClient";
import AuthShell from "components/auth/AuthShell";
import AuthBrand from "components/auth/AuthBrand";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import { clearPostLoginRedirect, resolvePostLoginRedirect } from "utils/postLoginRedirect";
import { createWorkspace, fetchMemberWorkspaces } from "services/workspaceService";
import { OLIVEEB_COLORS } from "workspaces/colors";
import {
  assertAppleRedirectUri,
  getAppleClientId,
  getAppleRedirectUri,
} from "utils/appleAuth";

const { Paragraph, Text, Title } = Typography;

const starterStorageKey = "ovacusInitialWorkspaceDraft";
const globalSelectedWorkspaceKey = "selectedWorkspaceId";
const globalSelectedWorkspaceReadyKey = "selectedWorkspaceReady";
const selectedWorkspaceKeyFor = (userId) => `selectedWorkspaceId:${userId || "anonymous"}`;

const steps = [
  {
    key: "account",
    title: "Choose account type",
    reason: "Select whether this account is for you or for an organization.",
  },
  {
    key: "intent",
    title: "How will you use Ovacus first?",
    reason: "Choose the first experience we should prepare for you.",
  },
  {
    key: "credentials",
    title: "Create your login",
    reason: "Use email and password, or choose a social account for faster setup.",
  },
  {
    key: "name",
    title: "What is your name?",
    reason: "Add the first and last name for the person managing this account.",
  },
];

const accountTypeOptions = [
  {
    value: "personal",
    title: "Individual",
    description: "I am creating an Ovacus account for myself.",
    icon: <UserOutlined />,
    color: OLIVEEB_COLORS.oliveebBlue,
    soft: OLIVEEB_COLORS.oliveebBlueSoft,
  },
  {
    value: "organization",
    title: "Organization",
    description: "I am creating an Ovacus account for a company or team.",
    icon: <TeamOutlined />,
    color: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  },
];

const helpOptions = [
  {
    value: "engineering_projects",
    title: "Engineering projects",
    description: "Plan, assign, track, and ship software work.",
    icon: <CodeOutlined />,
    color: OLIVEEB_COLORS.oliveebBlue,
    soft: OLIVEEB_COLORS.oliveebBlueSoft,
  },
  {
    value: "agile_delivery",
    title: "Agile delivery",
    description: "Coordinate sprints, iterations, and team flow.",
    icon: <RocketOutlined />,
    color: OLIVEEB_COLORS.oliveebGreen,
    soft: OLIVEEB_COLORS.oliveebGreenSoft,
  },
  {
    value: "testing_validation",
    title: "Testing and validation",
    description: "Keep validation signals close to delivery.",
    icon: <CheckCircleOutlined />,
    color: OLIVEEB_COLORS.oliveebOrange,
    soft: OLIVEEB_COLORS.goldSoft,
  },
];

const accountTypeToIntent = (accountType) =>
  accountType === "organization" ? "agile_delivery" : "engineering_projects";

const clearStarterDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(starterStorageKey);
  } catch {
    // Local storage cleanup is best-effort.
  }
};

const wait = (durationMs) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, durationMs);
  });

const clearWorkspaceSelectionCache = () => {
  if (typeof window === "undefined") return;
  try {
    const keysToRemove = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (
        key === globalSelectedWorkspaceKey ||
        key?.startsWith("selectedWorkspaceId:") ||
        key?.startsWith("selectedWorkspaceUse:")
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
    window.localStorage.setItem(globalSelectedWorkspaceReadyKey, "1");
  } catch {
    // Workspace cache cleanup is best-effort.
  }
};

const cacheSelectedWorkspace = ({ workspace, actorId }) => {
  if (!workspace?.id || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(selectedWorkspaceKeyFor(actorId), workspace.id);
    window.localStorage.setItem(globalSelectedWorkspaceKey, workspace.id);
    window.localStorage.setItem(globalSelectedWorkspaceReadyKey, "1");
  } catch {
    // Workspace cache is best-effort; the workspace still exists server-side.
  }
};

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return {};

  try {
    const payloadPart = token.split(".")[1];
    if (!payloadPart) return {};
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
};

const splitName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const providerLabel = (provider) =>
  ({
    google: "Google",
    facebook: "Facebook",
    apple: "Apple",
    microsoft: "Microsoft",
    linkedin: "LinkedIn",
  })[provider] || "Social";

const canCompleteSocialSignup = (result) => {
  const details = result?.details || {};
  return Boolean(
    details?.requiresPassword ||
    details?.requiresLinkApproval ||
    details?.userInfo,
  );
};

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.VITE_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "822618703980688";
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "366258d5-f9f5-4591-81eb-22976b18775f";
const GOOGLE_SDK_SRC = "https://accounts.google.com/gsi/client";
const FACEBOOK_SDK_SRC = "https://connect.facebook.net/en_US/sdk.js";
const MICROSOFT_SDK_SRC =
  "https://cdn.jsdelivr.net/npm/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";
const MICROSOFT_SDK_SRC_FALLBACK =
  "https://unpkg.com/@azure/msal-browser@2.38.3/lib/msal-browser.min.js";
const APPLE_CLIENT_ID = getAppleClientId();
const APPLE_REDIRECT_URI = assertAppleRedirectUri(getAppleRedirectUri());
const APPLE_SDK_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const compactInputStyle = {
  height: 54,
  borderRadius: 999,
  borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
  background: "#fff",
  boxShadow: "0 14px 28px rgba(16,147,87,0.10)",
  color: OLIVEEB_COLORS.oliveebDarkBlue,
  fontSize: 17,
  fontWeight: 800,
};

const compactFormItemStyle = {
  marginBottom: 10,
};

const primaryActionStyle = {
  height: 46,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 15,
  background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebGreen} 0%, #19B86D 100%)`,
  borderColor: OLIVEEB_COLORS.oliveebGreen,
  boxShadow: "0 12px 24px rgba(16, 147, 87, 0.22)",
};

const formLabelStyle = `
  .ovacus-signup-form .ant-form-item-label > label {
    color: ${OLIVEEB_COLORS.oliveebGreen};
    font-size: 13px;
    font-weight: 900;
  }
  .ovacus-signup-form .ant-input,
  .ovacus-signup-form .ant-input-affix-wrapper,
  .ovacus-signup-form .ant-select-selector {
    border-radius: 999px !important;
    font-size: 17px;
    font-weight: 800;
    background: #fff !important;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue};
  }
  .ovacus-signup-form .ant-input-affix-wrapper {
    overflow: hidden;
  }
  .ovacus-signup-form .ant-form-item-control-input-content {
    filter: drop-shadow(0 8px 14px rgba(16,147,87,0.10));
  }
  .ovacus-signup-form .ant-input-affix-wrapper-focused,
  .ovacus-signup-form .ant-input-affix-wrapper:focus,
  .ovacus-signup-form .ant-select-focused .ant-select-selector {
    border-color: ${OLIVEEB_COLORS.oliveebGreen} !important;
    box-shadow: 0 0 0 4px rgba(16, 147, 87, 0.12) !important;
  }
  .ovacus-choice-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .ovacus-choice-grid.help {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .ovacus-signup-social {
    width: 100%;
    max-width: 390px;
    margin: 0 auto 12px;
  }
  .ovacus-signup-social .auth-social__card {
    display: grid;
    gap: 12px;
    justify-items: center;
    padding: 12px;
    border-radius: 22px;
    border: 1px solid ${OLIVEEB_COLORS.oliveebGreenSoft};
    background: linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(246,255,249,0.92) 100%);
    box-shadow: 0 16px 32px rgba(16,147,87,0.10);
  }
  .ovacus-signup-social .auth-social__google,
  .ovacus-signup-social .auth-social__provider,
  .ovacus-signup-social .auth-social__apple {
    width: 100%;
    max-width: 330px;
    min-height: 48px;
    border-radius: 999px !important;
    font-weight: 900 !important;
    box-shadow: 0 10px 20px rgba(15,23,42,0.08);
  }
  .ovacus-signup-social .auth-social__microsoft {
    border-color: #D7DEE9 !important;
    color: ${OLIVEEB_COLORS.oliveebDarkBlue} !important;
  }
  @media (max-width: 720px) {
    .ovacus-choice-grid,
    .ovacus-choice-grid.help {
      grid-template-columns: 1fr;
    }
    .ovacus-signup-social {
      max-width: 100%;
    }
  }
`;

let googleSdkPromise = null;
let facebookSdkPromise = null;
let appleSdkPromise = null;
let microsoftSdkPromise = null;

const AppleBrandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const FacebookBrandIcon = () => (
  <span style={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>f</span>
);

const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" rx="1" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" rx="1" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" rx="1" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" rx="1" />
  </svg>
);

const LinkedInLogo = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const loadScript = (src, promiseRefSetter) => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Social sign-up is only available in the browser."));
  }

  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load social sign-up.")), { once: true });
      if (existing.dataset.loaded === "true") resolve();
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      promiseRefSetter(null);
      reject(new Error("Failed to load social sign-up."));
    };
    document.body.appendChild(script);
  });
};

const loadGoogleSdk = () => {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!googleSdkPromise) {
    googleSdkPromise = loadScript(GOOGLE_SDK_SRC, (value) => {
      googleSdkPromise = value;
    });
  }
  return googleSdkPromise;
};

const loadAppleSdk = () => {
  if (window.AppleID?.auth) return Promise.resolve();
  if (!appleSdkPromise) {
    appleSdkPromise = loadScript(APPLE_SDK_SRC, (value) => {
      appleSdkPromise = value;
    });
  }
  return appleSdkPromise;
};

const loadFacebookSdk = () => {
  if (window.FB) return Promise.resolve();
  if (!facebookSdkPromise) {
    facebookSdkPromise = loadScript(FACEBOOK_SDK_SRC, (value) => {
      facebookSdkPromise = value;
    }).then(() => {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v19.0",
      });
    });
  }
  return facebookSdkPromise;
};

const loadMicrosoftSdk = (useFallback = false) => {
  const scriptSrc = useFallback ? MICROSOFT_SDK_SRC_FALLBACK : MICROSOFT_SDK_SRC;
  const sourceLabel = useFallback ? "fallback" : "primary";
  if (window.msal?.PublicClientApplication) return Promise.resolve();
  const existing = document.querySelector(`script[src="${scriptSrc}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50;
      const interval = setInterval(() => {
        if (window.msal?.PublicClientApplication) {
          clearInterval(interval);
          resolve();
        } else if (++attempts >= maxAttempts) {
          clearInterval(interval);
          if (!useFallback) {
            microsoftSdkPromise = null;
            loadMicrosoftSdk(true).then(resolve).catch(reject);
          } else {
            reject(new Error(`Microsoft SDK (${sourceLabel}) loaded but msal.PublicClientApplication not available.`));
          }
        }
      }, 100);
    });
  }
  if (!microsoftSdkPromise) {
    microsoftSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = scriptSrc;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setTimeout(() => {
          let attempts = 0;
          const maxAttempts = 50;
          const interval = setInterval(() => {
            if (window.msal?.PublicClientApplication) {
              clearInterval(interval);
              resolve();
            } else if (++attempts >= maxAttempts) {
              clearInterval(interval);
              if (!useFallback) {
                microsoftSdkPromise = null;
                loadMicrosoftSdk(true).then(resolve).catch(reject);
              } else {
                reject(new Error(`Microsoft SDK (${sourceLabel}) loaded but msal.PublicClientApplication not available.`));
              }
            }
          }, 100);
        }, 100);
      };
      script.onerror = () => {
        if (!useFallback) {
          microsoftSdkPromise = null;
          loadMicrosoftSdk(true).then(resolve).catch(reject);
        } else {
          reject(new Error(`Failed to load Microsoft SDK from ${sourceLabel} source.`));
        }
      };
      document.body.appendChild(script);
    });
  }
  return microsoftSdkPromise;
};

const GoogleSignupButton = ({ disabled, onSuccess, onError }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const renderButton = async () => {
      try {
        if (!GOOGLE_CLIENT_ID) throw new Error("Google sign-up is not configured.");
        await loadGoogleSdk();
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            if (response?.credential) onSuccess(response.credential);
            else onError(new Error("No Google credential returned."));
          },
        });
        containerRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: "outline",
          size: "large",
          text: "signup_with",
          shape: "pill",
          width: 300,
          logo_alignment: "left",
        });
      } catch (error) {
        onError(error);
      }
    };

    renderButton();
    return () => {
      cancelled = true;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [onError, onSuccess]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        maxWidth: 300,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    />
  );
};

const ChoiceCard = ({ option, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      border: `1px solid ${selected ? option.color : OLIVEEB_COLORS.oliveebGreenSoft}`,
      background: selected
        ? `linear-gradient(145deg, ${option.soft} 0%, #FFFFFF 100%)`
        : "linear-gradient(145deg, #FFFFFF 0%, #F8FFFB 100%)",
      borderRadius: 20,
      padding: 14,
      textAlign: "left",
      cursor: "pointer",
      minHeight: 118,
      boxShadow: selected
        ? "0 16px 32px rgba(16,147,87,0.15)"
        : "0 10px 20px rgba(16,147,87,0.08)",
      color: OLIVEEB_COLORS.oliveebDarkBlue,
      width: "100%",
    }}
  >
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: option.color,
            background: option.soft,
            fontSize: 17,
          }}
        >
          {option.icon}
        </span>
        {selected ? <CheckCircleOutlined style={{ color: option.color, fontSize: 18 }} /> : null}
      </Space>
      <Text style={{ display: "block", color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 15, fontWeight: 900 }}>
        {option.title}
      </Text>
      <Text style={{ display: "block", color: OLIVEEB_COLORS.textMuted, fontSize: 12, lineHeight: 1.45 }}>
        {option.description}
      </Text>
    </Space>
  </button>
);

const AuthSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    user,
    isAuthenticated,
    loginWithSocialProvider,
  } = useAuth();
  const { refreshWorkspaces, selectWorkspace } = useWorkspace();
  const [form] = Form.useForm();
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preparingAccount, setPreparingAccount] = useState(false);
  const [pendingWorkspaceSetup, setPendingWorkspaceSetup] = useState(null);
  const [workspaceSetupBlocked, setWorkspaceSetupBlocked] = useState(false);
  const [socialLoading, setSocialLoading] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [socialProvider, setSocialProvider] = useState("");
  const [socialData, setSocialData] = useState(null);
  const signupLaunchHandledRef = useRef(false);
  const currentStep = steps[stepIndex];
  const isSocialSignup = Boolean(socialProvider);
  const onboardingDraft = useMemo(() => {
    if (location.state?.onboardingDraft) return location.state.onboardingDraft;
    try {
      return JSON.parse(window.localStorage.getItem(starterStorageKey) || "null");
    } catch {
      return null;
    }
  }, [location.state?.onboardingDraft]);
  const starterCredentialMethod =
    location.state?.signupCredentialMethod ||
    (onboardingDraft?.credentialMethod === "social_login"
      ? "social"
      : onboardingDraft?.credentialMethod === "username_password"
        ? "form"
        : "");

  useEffect(() => {
    if (!onboardingDraft) return;
    try {
      window.localStorage.setItem(starterStorageKey, JSON.stringify(onboardingDraft));
    } catch {
      // Route state still keeps the starter context for this signup session.
    }
  }, [onboardingDraft]);

  useEffect(() => {
    if (!onboardingDraft || location.state?.socialDraft) return;
    const accountType =
      onboardingDraft.workspaceCreation?.accountType === "ORGANIZATION" ||
      onboardingDraft.usageType === "team" ||
      onboardingDraft.usageType === "enterprise"
        ? "organization"
        : "personal";
    const draftHelpFirst = Array.isArray(onboardingDraft.deliveryFocus)
      ? onboardingDraft.deliveryFocus[0]
      : onboardingDraft.deliveryFocus || accountTypeToIntent(accountType);
    form.setFieldsValue({
      accountType,
      helpFirst: draftHelpFirst,
      organizationName: accountType === "organization" ? onboardingDraft.workspaceName || "" : "",
      email: onboardingDraft.email || "",
    });
  }, [form, location.state?.socialDraft, onboardingDraft]);

  useEffect(() => {
    if (!onboardingDraft || location.state?.socialDraft || signupLaunchHandledRef.current) return;
    const credentialMethod = starterCredentialMethod;
    if (!credentialMethod) return;

    signupLaunchHandledRef.current = true;
    if (credentialMethod === "form" && location.state?.signupCredentials) {
      const signupCredentials = location.state.signupCredentials;
      form.setFieldsValue({
        email: signupCredentials.email || onboardingDraft.email || "",
        password: signupCredentials.password || "",
        confirmPassword: signupCredentials.password || "",
      });
      setStepIndex(Math.max(steps.findIndex((step) => step.key === "name"), 0));
      return;
    }

    setStepIndex(Math.max(steps.findIndex((step) => step.key === "credentials"), 0));
  }, [
    form,
    onboardingDraft,
    location.state?.signupCredentials,
    location.state?.socialDraft,
    starterCredentialMethod,
  ]);

  useEffect(() => {
    const draft = location.state?.socialDraft;
    if (!draft?.provider || !draft?.authPayload) return;

    setSocialProvider(draft.provider);
    setSocialData({
      authPayload: draft.authPayload,
      details: draft.details || {},
      userInfo: draft.userInfo || {},
    });
    form.setFieldsValue({
      email: draft.prefill?.email || draft.userInfo?.email || "",
      firstName: draft.prefill?.firstName || draft.userInfo?.firstName || "",
      lastName: draft.prefill?.lastName || draft.userInfo?.lastName || "",
    });
    setSubmitError(
      draft.details?.message ||
      `${providerLabel(draft.provider)} verified. Complete these details to finish your Ovacus account.`,
    );
    setStepIndex(Math.max(steps.findIndex((step) => step.key === "name"), 0));
  }, [form, location.state?.socialDraft]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !loading &&
      !preparingAccount &&
      !pendingWorkspaceSetup &&
      !onboardingDraft &&
      !workspaceSetupBlocked
    ) {
      navigate(paths.pmDashboard, { replace: true });
    }
  }, [
    isAuthenticated,
    loading,
    navigate,
    onboardingDraft,
    pendingWorkspaceSetup,
    preparingAccount,
    workspaceSetupBlocked,
  ]);

  const values = Form.useWatch([], form) || {};
  const selectedAccountType = values.accountType || "personal";
  const socialOnlyCredentialStep =
    currentStep.key === "credentials" &&
    starterCredentialMethod === "social" &&
    !isSocialSignup;
  const signupProgress = Math.round(((stepIndex + 1) / steps.length) * 100);
  const stepTitle = socialOnlyCredentialStep ? "Choose your social login" : currentStep.title;
  const stepReason = socialOnlyCredentialStep
    ? "Continue with one of these providers."
    : currentStep.reason;

  const getWorkspaceCreationPayload = (allValues = form.getFieldsValue(true)) => {
    const draftWorkspace = onboardingDraft?.workspaceCreation || {};
    const accountType = allValues.accountType || "personal";
    const organizationName =
      accountType === "organization"
        ? String(allValues.organizationName || "").trim()
        : "";
    const workspaceAccountType =
      draftWorkspace.accountType || (accountType === "organization" ? "ORGANIZATION" : "PERSONAL");
    const workspaceName =
      draftWorkspace.name ||
      organizationName ||
      (accountType === "organization"
        ? "Team delivery workspace"
        : "Personal project workspace");

    return {
      accountType: workspaceAccountType,
      name: workspaceName,
      organizationId: draftWorkspace.organizationId || undefined,
      organizationName: draftWorkspace.organizationName || organizationName || undefined,
      onboardingIntent: allValues.helpFirst || accountTypeToIntent(accountType),
      product: draftWorkspace.product || "OLIVEEB_OVACUS_SERVICE",
    };
  };

  const prepareAccountWorkspace = async ({ signedInUser, workspaceCreation }) => {
    const actorId = signedInUser?.id || signedInUser?.userId || "";
    if (!actorId || !workspaceCreation?.name) {
      clearStarterDraft();
      return null;
    }

    setPreparingAccount(true);
    const existingWorkspacePage = await fetchMemberWorkspaces({
      actorId,
      page: 0,
      size: 1,
    });
    const existingWorkspace = existingWorkspacePage?.content?.[0];
    if (existingWorkspace?.id) {
      cacheSelectedWorkspace({ workspace: existingWorkspace, actorId });
      clearStarterDraft();
      return existingWorkspace;
    }

    const workspace = await createWorkspace({
      actorId,
      name: workspaceCreation.name,
      description:
        workspaceCreation.accountType === "ORGANIZATION"
          ? "Created from Ovacus team onboarding."
          : "Created from Ovacus personal onboarding.",
      accountType: workspaceCreation.accountType || "PERSONAL",
      organizationId: workspaceCreation.organizationId,
    });

    cacheSelectedWorkspace({ workspace, actorId });
    clearStarterDraft();
    return workspace;
  };

  const verifyWorkspaceReady = useCallback(async (workspaceId) => {
    if (!workspaceId) return [];

    let accounts = [];
    for (let attempt = 0; attempt < 10; attempt += 1) {
      accounts = await refreshWorkspaces();
      if (accounts.some((account) => account?.id === workspaceId)) {
        return accounts;
      }
      await wait(700);
    }

    throw new Error("Your workspace is still being prepared. Please wait a moment and try again.");
  }, [refreshWorkspaces]);

  useEffect(() => {
    if (!pendingWorkspaceSetup || !isAuthenticated) return;
    const signedInUser = user || pendingWorkspaceSetup.signedInUser;
    if (!signedInUser) return;

    let cancelled = false;
    const runWorkspaceSetup = async () => {
      try {
        setPreparingAccount(true);
        setWorkspaceSetupBlocked(false);
        const preparedWorkspace = await prepareAccountWorkspace({
          signedInUser,
          workspaceCreation: pendingWorkspaceSetup.workspaceCreation,
        });
        if (preparedWorkspace?.id) {
          selectWorkspace(preparedWorkspace.id);
        }
        await verifyWorkspaceReady(preparedWorkspace?.id);
        if (preparedWorkspace?.id) {
          selectWorkspace(preparedWorkspace.id);
        }
        if (cancelled) return;
        message.success(pendingWorkspaceSetup.successMessage || "Account created successfully.");
        navigate(pendingWorkspaceSetup.redirectTo, { replace: true });
      } catch (error) {
        if (cancelled) return;
        setPreparingAccount(false);
        setWorkspaceSetupBlocked(true);
        const apiMessage =
          error?.response?.data?.humanMessage ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Workspace setup failed. Please try again.";
        setSubmitError(apiMessage);
        message.error(apiMessage);
      } finally {
        if (!cancelled) {
          setPendingWorkspaceSetup(null);
          setLoading(false);
        }
      }
    };

    runWorkspaceSetup();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    navigate,
    pendingWorkspaceSetup,
    selectWorkspace,
    user,
    verifyWorkspaceReady,
  ]);

  const validateCurrentStep = async () => {
    if (currentStep.key === "account") {
      await form.validateFields(["accountType"]);
      if ((form.getFieldValue("accountType") || "personal") === "organization") {
        await form.validateFields(["organizationName"]);
      }
      return;
    }

    if (currentStep.key === "intent") {
      await form.validateFields(["helpFirst"]);
      return;
    }

    if (currentStep.key === "credentials") {
      if (socialOnlyCredentialStep) return;
      await form.validateFields(["email"]);
      if (isSocialSignup) return;
      await form.validateFields(["password", "confirmPassword"]);

      const password = form.getFieldValue("password");
      const confirmPassword = form.getFieldValue("confirmPassword");
      if (password !== confirmPassword) {
        throw new Error("Passwords do not match.");
      }
      return;
    }

    if (currentStep.key === "name") {
      await form.validateFields(["firstName", "lastName"]);
      return;
    }
  };

  const goNext = async () => {
    try {
      await validateCurrentStep();
      setSubmitError("");
      setStepIndex((index) => Math.min(index + 1, steps.length - 1));
    } catch (error) {
      message.error(error?.message || "Please complete the required details.");
    }
  };

  const goBack = () => {
    const nameStepIndex = Math.max(steps.findIndex((step) => step.key === "name"), 0);
    const credentialsStepIndex = Math.max(steps.findIndex((step) => step.key === "credentials"), 0);
    const navigateToGetStartedStep = (currentStepKey) => {
      const nextDraft = onboardingDraft
        ? {
            ...onboardingDraft,
            currentStepKey,
          }
        : null;

      navigate(paths.getStarted, {
        state: {
          ...(location.state || {}),
          ...(nextDraft ? { onboardingDraft: nextDraft } : {}),
        },
      });
    };

    if (
      onboardingDraft &&
      starterCredentialMethod === "form" &&
      stepIndex === nameStepIndex &&
      location.state?.signupCredentials
    ) {
      navigateToGetStartedStep("loginCredentials");
      return;
    }

    if (
      onboardingDraft &&
      starterCredentialMethod === "social" &&
      stepIndex === credentialsStepIndex &&
      !isSocialSignup
    ) {
      navigateToGetStartedStep("credentialMethod");
      return;
    }

    if (stepIndex === 0) {
      navigate(onboardingDraft ? paths.getStarted : paths.authLanding, { state: location.state });
      return;
    }
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const resetSignupFlow = () => {
    clearStarterDraft();
    setSocialProvider("");
    setSocialData(null);
    setSubmitError("");
    setSocialLoading("");
    setPendingWorkspaceSetup(null);
    setPreparingAccount(false);
    setWorkspaceSetupBlocked(false);
    signupLaunchHandledRef.current = true;
    form.resetFields();
    form.setFieldsValue({
      accountType: "personal",
      helpFirst: "engineering_projects",
    });
    setStepIndex(0);
    navigate(paths.simpleSignup, { replace: true, state: null });
  };

  const goToSignIn = () => {
    clearStarterDraft();
    setSocialProvider("");
    setSocialData(null);
    setSubmitError("");
    setPendingWorkspaceSetup(null);
    setPreparingAccount(false);
    setWorkspaceSetupBlocked(false);
    navigate(paths.simpleLogin, { state: null });
  };

  const handleSignup = async () => {
    try {
      await form.validateFields(["accountType", "helpFirst", "email", "firstName", "lastName"]);
      if ((form.getFieldValue("accountType") || "personal") === "organization") {
        await form.validateFields(["organizationName"]);
      }
      if (!isSocialSignup) {
        await form.validateFields(["password", "confirmPassword"]);
        const password = form.getFieldValue("password");
        const confirmPassword = form.getFieldValue("confirmPassword");
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match.");
        }
      }
      await validateCurrentStep();
      setLoading(true);
      setSubmitError("");
      setWorkspaceSetupBlocked(false);

      const allValues = form.getFieldsValue(true);
      const accountType = allValues.accountType || "personal";
      const onboardingIntent = allValues.helpFirst || accountTypeToIntent(accountType);
      const organizationName =
        accountType === "organization" ? String(allValues.organizationName || "").trim() : "";
      const workspaceCreation = getWorkspaceCreationPayload(allValues);
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      if (isSocialSignup) {
        const body = {
          ...(socialData?.authPayload || {}),
          email: allValues.email?.trim(),
          firstName: allValues.firstName?.trim(),
          lastName: allValues.lastName?.trim(),
          username: allValues.email?.trim(),
          isSocial: true,
          type: "CUSTOMER",
          accountType,
          organizationName: organizationName || undefined,
          onboardingIntent,
          currentAppRole: "APP_RESTRICTED_USER",
        };

        if (!body.email) {
          throw new Error(`${providerLabel(socialProvider)} sign-up needs an email address.`);
        }

        const result = await loginWithSocialProvider(socialProvider, body);
        if (!result?.success) {
          const details = result?.details || {};
          if (details?.requiresLinkApproval) {
            beginSocialSignup(socialProvider, socialData?.authPayload || body, details, {
              email: body.email,
              firstName: body.firstName,
              lastName: body.lastName,
            });
            return;
          }
          throw new Error(result?.error || `${providerLabel(socialProvider)} sign-up failed.`);
        }

        setPreparingAccount(true);
        setPendingWorkspaceSetup({
          signedInUser: result.user || result.data,
          workspaceCreation,
          redirectTo: paths.pmDashboard,
          successMessage: `${providerLabel(socialProvider)} account created successfully.`,
        });
        return;
      }

      const payload = {
        firstName: allValues.firstName.trim(),
        lastName: allValues.lastName.trim(),
        email: allValues.email.trim(),
        username: allValues.email.trim(),
        password: allValues.password,
        isSocial: false,
        type: "CUSTOMER",
        accountType,
        organizationName: organizationName || undefined,
        onboardingIntent,
        currentAppRole: "APP_RESTRICTED_USER",
      };

      await apiClient.post("/identity-service/api/v1/auth/signup", payload, {
        headers: {
          "O-SKIP-WORKSPACE-CONTEXT": "1",
        },
      });

      const loginResult = await login(payload.email, payload.password);
      if (!loginResult?.success) {
        message.success("Account created. Please sign in to continue.");
        navigate(paths.simpleLogin, { replace: true, state: location.state });
        return;
      }

      setPreparingAccount(true);
      setPendingWorkspaceSetup({
        signedInUser: loginResult.user || loginResult.data,
        workspaceCreation,
        redirectTo: resolvePostLoginRedirect(location, paths.pmDashboard),
        successMessage: "Account created successfully.",
      });
    } catch (error) {
      setPreparingAccount(false);
      const apiMessage =
        error?.response?.data?.humanMessage ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Signup failed. Please try again.";
      setSubmitError(apiMessage);
      message.error(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const queueWorkspaceSetupAfterSocialAuth = (provider, result) => {
    setPreparingAccount(true);
    setPendingWorkspaceSetup({
      signedInUser: result?.user || result?.data,
      workspaceCreation: getWorkspaceCreationPayload(form.getFieldsValue(true)),
      redirectTo: paths.pmDashboard,
      successMessage: `${providerLabel(provider)} account created successfully.`,
    });
  };

  const beginSocialSignup = (provider, authPayload, details = {}, fallbackProfile = {}) => {
    const userInfo = details?.userInfo || fallbackProfile || {};
    const jwtPayload = authPayload?.idToken || authPayload?.identityToken
      ? decodeJwtPayload(authPayload.idToken || authPayload.identityToken)
      : {};
    if (details?.requiresLinkApproval) {
      navigate(paths.socialSetup || paths.appleSetup, {
        state: {
          mode: "link",
          provider,
          authPayload,
          providerUserId:
            details?.providerUserId ||
            details?.googleUserId ||
            details?.appleUserId ||
            details?.linkedinUserId ||
            authPayload?.providerUserId ||
            jwtPayload?.sub ||
            jwtPayload?.oid ||
            "",
          userInfo,
          message:
            details?.message ||
            `An account already exists with this email. Confirm linking to continue with ${providerLabel(provider)}.`,
          linkingProcessId: details?.linkingProcessId || "",
          requiresPassword: !!details?.requiresPassword,
          requiresLinkApproval: true,
        },
      });
      return;
    }

    const split = splitName(
      userInfo.name ||
      jwtPayload.name ||
      [userInfo.firstName, userInfo.lastName].filter(Boolean).join(" "),
    );
    const firstName =
      userInfo.firstName ||
      userInfo.givenName ||
      jwtPayload.given_name ||
      split.firstName ||
      "";
    const lastName =
      userInfo.lastName ||
      userInfo.familyName ||
      jwtPayload.family_name ||
      split.lastName ||
      "";
    const email =
      userInfo.email ||
      details?.email ||
      authPayload?.email ||
      jwtPayload.email ||
      "";

    setSocialProvider(provider);
    setSocialData({ authPayload, details, userInfo });
    form.setFieldsValue({
      email,
      firstName,
      lastName,
    });
    setStepIndex(Math.max(steps.findIndex((step) => step.key === "name"), 0));
    setSubmitError(
      details?.message ||
      `${providerLabel(provider)} verified. Complete these details to finish your Ovacus account.`,
    );
  };

  const handleGoogleSignup = async (idToken) => {
    try {
      setSocialLoading("google");
      const authPayload = { idToken };
      const jwtPayload = decodeJwtPayload(idToken);
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      const result = await loginWithSocialProvider("google", authPayload);
      if (!result?.success) {
        if (!canCompleteSocialSignup(result)) {
          throw new Error(result?.error || "Google sign-up failed.");
        }
        beginSocialSignup("google", authPayload, result?.details, {
          email: jwtPayload.email,
          name: jwtPayload.name,
          firstName: jwtPayload.given_name,
          lastName: jwtPayload.family_name,
          picture: jwtPayload.picture,
        });
        return;
      }
      queueWorkspaceSetupAfterSocialAuth("google", result);
    } catch (error) {
      message.error(error?.message || "Google sign-up failed.");
    } finally {
      setSocialLoading("");
    }
  };

  const handleAppleSignup = async () => {
    try {
      setSocialLoading("apple");
      await loadAppleSdk();

      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true,
        state:
          window.crypto && typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : `apple-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      });

      const appleResponse = await window.AppleID.auth.signIn();
      const code = appleResponse?.authorization?.code || appleResponse?.code;
      const identityToken =
        appleResponse?.authorization?.id_token ||
        appleResponse?.authorization?.idToken ||
        appleResponse?.identityToken ||
        "";
      const jwtPayload = decodeJwtPayload(identityToken);
      const authPayload = {
        identityToken,
        authorizationCode: code,
        user: appleResponse?.user,
        fullName: appleResponse?.user?.name,
        email: appleResponse?.user?.email || jwtPayload.email,
        redirectUri: APPLE_REDIRECT_URI,
      };
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      const result = await loginWithSocialProvider("apple", authPayload);

      if (!result?.success) {
        const details = result?.details || {};
        if (details?.requiresPassword || details?.requiresLinkApproval) {
          beginSocialSignup("apple", authPayload, details, {
            email: authPayload.email,
            firstName: appleResponse?.user?.name?.firstName || jwtPayload.given_name,
            lastName: appleResponse?.user?.name?.lastName || jwtPayload.family_name,
          });
          return;
        }
        if (canCompleteSocialSignup(result)) {
          beginSocialSignup("apple", authPayload, details, {
            email: authPayload.email,
            firstName: appleResponse?.user?.name?.firstName || jwtPayload.given_name,
            lastName: appleResponse?.user?.name?.lastName || jwtPayload.family_name,
          });
          return;
        }
        throw new Error(result?.error || "Apple sign-up failed.");
      }

      queueWorkspaceSetupAfterSocialAuth("apple", result);
    } catch (error) {
      const text = String(error?.message || error?.error || "").toLowerCase();
      if (!text.includes("cancel")) {
        message.error(error?.message || "Apple sign-up failed.");
      }
    } finally {
      setSocialLoading("");
    }
  };

const handleMicrosoftSignup = async () => {
    try {
      setSocialLoading("microsoft");
      if (!MICROSOFT_CLIENT_ID) throw new Error("Microsoft sign-up is not configured.");

      await loadMicrosoftSdk();

      const msalInstance = new window.msal.PublicClientApplication({
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: "https://login.microsoftonline.com/common",
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: "sessionStorage",
          storeAuthStateInCookie: false,
        },
      });
      
      let response;
      try {
        response = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "email"],
          prompt: "select_account",
        });
      } catch (popupErr) {
        const errMsg = String(popupErr?.message || popupErr?.errorMessage || "").toLowerCase();
        if (errMsg.includes("popup") || errMsg.includes("window") || errMsg.includes("blocked")) {
          throw new Error("Microsoft sign-up popup was blocked. Please allow popups for this site and try again.");
        }
        throw popupErr;
      }

      console.log("Microsoft signup response:", response);
      const idToken = response?.idToken;
      if (!idToken) {
        console.error("Microsoft signup response:", JSON.stringify(response, null, 2));
        throw new Error("No Microsoft ID token returned. Check browser console for details.");
      }

      const account = response?.account || {};
      const authPayload = { idToken };
      const jwtPayload = decodeJwtPayload(idToken);
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      const result = await loginWithSocialProvider("microsoft", authPayload);
      if (!result?.success) {
        if (!canCompleteSocialSignup(result)) {
          throw new Error(result?.error || "Microsoft sign-up failed.");
        }
        beginSocialSignup("microsoft", authPayload, result?.details, {
          email: account.username || jwtPayload.email || jwtPayload.preferred_username,
          name: account.name || jwtPayload.name,
          firstName: jwtPayload.given_name,
          lastName: jwtPayload.family_name,
        });
        return;
      }
      queueWorkspaceSetupAfterSocialAuth("microsoft", result);
    } catch (error) {
      const text = String(error?.message || error?.errorMessage || "").toLowerCase();
      if (!text.includes("user_cancelled") && !text.includes("cancel")) {
        message.error(error?.message || error?.errorMessage || "Microsoft sign-up failed.");
      }
    } finally {
      setSocialLoading("");
    }
  };

  const handleLinkedInSignup = async () => {
    try {
      setSocialLoading("linkedin");
      const returnTo = `${window.location.origin}${paths.linkedInCallback}?next=${encodeURIComponent(paths.pmDashboard)}`;
      const { data } = await apiClient.get("/identity-service/api/v1/auth/linkedin/web", {
        params: { returnTo },
      });
      const redirectUrl = data?.redirect_url || data?.redirectUrl;
      if (!redirectUrl) throw new Error("LinkedIn sign-up URL was not returned.");
      window.location.assign(redirectUrl);
    } catch (error) {
      message.error(error?.message || "LinkedIn sign-up failed.");
      setSocialLoading("");
    }
  };

  const handleFacebookSignup = async () => {
    try {
      setSocialLoading("facebook");
      await loadFacebookSdk();
      const facebookResponse = await new Promise((resolve, reject) => {
        window.FB.login(
          (response) => {
            if (response?.authResponse?.accessToken) {
              resolve(response);
            } else {
              reject(new Error("Facebook sign-up was cancelled or did not return a token."));
            }
          },
          { scope: "email,public_profile" },
        );
      });

      const facebookProfile = await new Promise((resolve) => {
        window.FB.api(
          "/me",
          { fields: "id,first_name,last_name,name,email,picture" },
          (profile) => resolve(profile || {}),
        );
      });
      const authPayload = { accessToken: facebookResponse.authResponse.accessToken };
      clearWorkspaceSelectionCache();
      clearPostLoginRedirect();
      const result = await loginWithSocialProvider("facebook", authPayload);
      if (!result?.success) {
        if (!canCompleteSocialSignup(result)) {
          throw new Error(result?.error || "Facebook sign-up failed.");
        }
        beginSocialSignup("facebook", authPayload, result?.details, {
          email: facebookProfile.email,
          name: facebookProfile.name,
          firstName: facebookProfile.first_name,
          lastName: facebookProfile.last_name,
          picture: facebookProfile.picture?.data?.url,
        });
        return;
      }
      queueWorkspaceSetupAfterSocialAuth("facebook", result);
    } catch (error) {
      message.error(error?.message || "Facebook sign-up failed.");
    } finally {
      setSocialLoading("");
    }
  };

  return (
    <AuthShell mode="center">
      <style>{formLabelStyle}</style>
      <Card
        variant="borderless"
        style={{
          width: "min(100%, 620px)",
          borderRadius: 28,
          border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
          boxShadow: "0 26px 72px rgba(0, 82, 64, 0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
          background: "linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(246,255,249,0.96) 58%, rgba(255,253,235,0.94) 100%)",
          overflow: "hidden",
        }}
        styles={{ body: { padding: 24 } }}
      >
        <Space direction="vertical" size={18} style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={goBack}
              shape="circle"
              style={{
                color: OLIVEEB_COLORS.oliveebBlue,
                borderColor: OLIVEEB_COLORS.oliveebBlue,
                background: OLIVEEB_COLORS.white,
                boxShadow: "0 10px 22px rgba(0,87,184,0.12)",
              }}
            />
            <div
              style={{
                padding: "7px 14px",
                borderRadius: 24,
                background: OLIVEEB_COLORS.white,
                border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                boxShadow: "0 12px 24px rgba(39,174,48,0.14)",
              }}
            >
              <AuthBrand centered size={78} />
            </div>
            <Space size={8}>
              <Button
                type="link"
                onClick={resetSignupFlow}
                disabled={loading || preparingAccount}
                style={{ color: OLIVEEB_COLORS.textMuted, fontWeight: 900, paddingInline: 0 }}
              >
                Reset
              </Button>
              <Button
                type="link"
                onClick={goToSignIn}
                style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 900, paddingInline: 0 }}
              >
                Sign in
              </Button>
            </Space>
          </div>

          <Progress
            percent={signupProgress}
            showInfo={false}
            strokeColor={OLIVEEB_COLORS.oliveebGreen}
            trailColor={OLIVEEB_COLORS.oliveebGreenSoft}
          />

          <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center" }}>
            <Text
              style={{
                color: OLIVEEB_COLORS.oliveebGreen,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontSize: 11,
              }}
            >
              Create account
            </Text>
            <Title
              level={2}
              style={{
                color: OLIVEEB_COLORS.oliveebGreen,
                margin: 0,
                fontWeight: 900,
                lineHeight: 1.12,
              }}
            >
              {stepTitle}
            </Title>
            <Paragraph
              style={{
                margin: 0,
                color: OLIVEEB_COLORS.oliveebBlue,
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {isSocialSignup && currentStep.key === "name"
                ? `${providerLabel(socialProvider)} verified. Add your name to finish your Ovacus account.`
                : stepReason}
            </Paragraph>
          </Space>

          {submitError ? (
            <Alert
              type="warning"
              showIcon
              message="Signup needs attention"
              description={submitError}
              style={{ borderRadius: 14, padding: "8px 12px" }}
            />
          ) : null}

          <Form
            form={form}
            className="ovacus-signup-form"
            layout="vertical"
            requiredMark={false}
            initialValues={{
              helpFirst: "engineering_projects",
              accountType: "personal",
            }}
            size="middle"
          >
            <div style={{ display: currentStep.key === "account" ? "block" : "none" }}>
              <Form.Item
                name="accountType"
                rules={[{ required: true, message: "Choose who you are signing up as" }]}
                style={{ marginBottom: 12 }}
              >
                <Input type="hidden" />
              </Form.Item>
              <div className="ovacus-choice-grid">
                {accountTypeOptions.map((option) => (
                  <ChoiceCard
                    key={option.value}
                    option={option}
                    selected={(values.accountType || "personal") === option.value}
                    onClick={() => {
                      form.setFieldsValue({
                        accountType: option.value,
                        helpFirst: accountTypeToIntent(option.value),
                      });
                    }}
                  />
                ))}
              </div>

              <div style={{ marginTop: 14 }}>
                {selectedAccountType === "organization" ? (
                  <Form.Item
                    label="Organization name"
                    name="organizationName"
                    style={compactFormItemStyle}
                    rules={[{ required: true, message: "Enter your organization name" }]}
                  >
                    <Input
                      prefix={<TeamOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Organization or team name"
                      autoComplete="organization"
                      style={compactInputStyle}
                    />
                  </Form.Item>
                ) : null}
              </div>
            </div>

            <div style={{ display: currentStep.key === "intent" ? "block" : "none" }}>
              <Form.Item
                name="helpFirst"
                rules={[{ required: true, message: "Choose how you will use Ovacus first" }]}
                style={{ marginBottom: 0 }}
              >
                <Input type="hidden" />
              </Form.Item>
              <div className="ovacus-choice-grid help">
                {helpOptions.map((option) => (
                  <ChoiceCard
                    key={option.value}
                    option={option}
                    selected={(values.helpFirst || accountTypeToIntent(selectedAccountType)) === option.value}
                    onClick={() => form.setFieldValue("helpFirst", option.value)}
                  />
                ))}
              </div>
            </div>

            <div style={{ display: currentStep.key === "credentials" ? "block" : "none" }}>
              {!socialOnlyCredentialStep ? (
                <Space direction="vertical" size={6} style={{ width: "100%", textAlign: "center", marginBottom: 12 }}>
                  <Text style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontWeight: 900 }}>
                    Create your login
                  </Text>
                  <Text style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12 }}>
                    Use email and password, or continue with a social account.
                  </Text>
                </Space>
              ) : null}

              <div className="auth-social auth-social--minimal ovacus-signup-social">
                <div className="auth-social__card">
                  <GoogleSignupButton
                    disabled={loading || Boolean(socialLoading)}
                    onSuccess={handleGoogleSignup}
                    onError={(error) => message.error(error?.message || "Google sign-up failed.")}
                  />
                  <Button
                    block
                    size="large"
                    className="auth-social__provider auth-social__microsoft"
                    loading={socialLoading === "microsoft"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<MicrosoftLogo />}
                    onClick={handleMicrosoftSignup}
                  >
                    Continue with Microsoft
                  </Button>
                  <Button
                    block
                    size="large"
                    className="auth-social__provider auth-social__linkedin"
                    loading={socialLoading === "linkedin"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<LinkedInLogo />}
                    onClick={handleLinkedInSignup}
                    style={{ background: "#0A66C2", borderColor: "#0A66C2", color: "#fff" }}
                  >
                    Continue with LinkedIn
                  </Button>
                  <Button
                    block
                    size="large"
                    className="auth-social__apple"
                    loading={socialLoading === "apple"}
                    disabled={loading || Boolean(socialLoading)}
                    icon={<AppleBrandIcon />}
                    onClick={handleAppleSignup}
                    style={{ background: "#000", borderColor: "#000", color: "#fff" }}
                  >
                    Continue with Apple
                  </Button>
                </div>
              </div>

              {!socialOnlyCredentialStep ? (
                <>
                  <Divider plain style={{ margin: "10px 0 8px", fontSize: 11 }}>
                    or create with email
                  </Divider>

                  <Form.Item
                    label="Username"
                    name="email"
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Enter your username" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="you@company.com"
                      autoComplete="email"
                      style={compactInputStyle}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Password"
                    name="password"
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Enter a password" },
                      { min: 8, message: "Password must be at least 8 characters" },
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Create a password"
                      autoComplete="new-password"
                      style={compactInputStyle}
                      iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Confirm password"
                    name="confirmPassword"
                    dependencies={["password"]}
                    style={compactFormItemStyle}
                    rules={[
                      { required: true, message: "Confirm your password" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Passwords do not match"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password
                      prefix={<LockOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                      placeholder="Confirm password"
                      autoComplete="new-password"
                      style={compactInputStyle}
                      iconRender={(visible) => visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    />
                  </Form.Item>
                </>
              ) : null}
            </div>

            <div style={{ display: currentStep.key === "name" ? "block" : "none" }}>
              <div className="ovacus-choice-grid">
                <Form.Item
                  label="First name"
                  name="firstName"
                  style={compactFormItemStyle}
                  rules={[{ required: true, message: "Enter your first name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    autoComplete="given-name"
                    style={compactInputStyle}
                  />
                </Form.Item>
                <Form.Item
                  label="Last name"
                  name="lastName"
                  style={compactFormItemStyle}
                  rules={[{ required: true, message: "Enter your last name" }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: OLIVEEB_COLORS.placeholder }} />}
                    autoComplete="family-name"
                    style={compactInputStyle}
                  />
                </Form.Item>
              </div>
            </div>
          </Form>

          <Space direction="vertical" size={6} style={{ width: "100%" }}>
            {socialOnlyCredentialStep ? null : currentStep.key === "name" ? (
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                loading={loading}
                onClick={handleSignup}
                style={primaryActionStyle}
              >
                Create account
              </Button>
            ) : (
              <Button
                type="primary"
                size="large"
                block
                onClick={goNext}
                style={primaryActionStyle}
              >
                Continue
              </Button>
            )}
            <Button
              type="link"
              onClick={() => navigate(paths.simpleLogin, { state: location.state })}
              style={{
                height: 28,
                padding: 0,
                fontWeight: 800,
                color: OLIVEEB_COLORS.oliveebBlue,
              }}
            >
              Already have an account? Sign in
            </Button>
          </Space>
        </Space>
      </Card>
    </AuthShell>
  );
};

export default AuthSignup;
